import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Trophy, 
  Target, 
  Users, 
  Calendar, 
  TrendingUp, 
  Award, 
  Clock, 
  BarChart3,
  Activity,
  Zap,
  Crown,
  Medal,
  Timer,
  CheckCircle,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, ProgressBar } from 'recharts';

const ProgressView = () => {
  const { type, id } = useParams(); // type: 'challenge' or 'goal', id: challenge/goal id
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [progressData, setProgressData] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchProgressData();
  }, [type, id]);

  const fetchProgressData = async () => {
    setLoading(true);
    try {
      if (type === 'challenge') {
        // Fetch challenge details and progress
        const [challengeRes, progressRes, participantsRes] = await Promise.all([
          axios.get(`/challenges/${id}`),
          axios.get(`/challenges/${id}/progress`),
          axios.get(`/challenges/${id}/participants`)
        ]);
        console.log('Challenge details:', challengeRes.data);
        console.log('Progress data:', progressRes.data);
        console.log('Participants data:', participantsRes.data);
        setData(challengeRes.data);
        setProgressData(progressRes.data);
        setParticipants(participantsRes.data);
      } else if (type === 'goal') {
        // Fetch goal details and progress
        const [goalRes, progressRes] = await Promise.all([
          axios.get(`/goals/${id}`),
          axios.get(`/goals/${id}/progress`)
        ]);
        console.log('Goal details:', goalRes.data);
        console.log('Goal progress:', progressRes.data);
        setData(goalRes.data);
        setProgressData(progressRes.data);
      }
    } catch (error) {
      console.error('Error fetching progress data:', error);
      // Show error state instead of fallback data
      setData(null);
      setProgressData([]);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = () => {
    if (!data || !progressData.length) return 0;
    
    if (type === 'challenge') {
      const latestProgress = progressData[progressData.length - 1]?.totalProgress || 0;
      return Math.min((latestProgress / data.target_value) * 100, 100);
    } else {
      const latestProgress = progressData[progressData.length - 1]?.progress || 0;
      return Math.min((latestProgress / data.target_value) * 100, 100);
    }
  };

  const getDaysRemaining = () => {
    if (!data?.end_date) return 0;
    
    try {
      const endDate = new Date(data.end_date);
      const today = new Date();
      
      // Check if date is valid
      if (isNaN(endDate.getTime())) return 0;
      
      const diffTime = endDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (error) {
      console.error('Error calculating days remaining:', error);
      return 0;
    }
  };

  const chartColors = {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6'
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Progress Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Overall Progress</p>
              <p className="text-3xl font-bold text-white">{calculateProgress().toFixed(1)}%</p>
            </div>
            <BarChart3 className="h-12 w-12 text-blue-200" />
          </div>
          <div className="mt-4">
            <div className="bg-blue-400 rounded-full h-2">
              <div 
                className="bg-white rounded-full h-2 transition-all duration-500"
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">
                {type === 'challenge' ? 'Participants' : 'Current Value'}
              </p>
              <p className="text-3xl font-bold text-white">
                {type === 'challenge' ? data?.participant_count || 0 : progressData[progressData.length - 1]?.progress || 0}
              </p>
            </div>
            <Users className="h-12 w-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium">Target</p>
              <p className="text-3xl font-bold text-white">{data?.target_value || 0}</p>
              <p className="text-yellow-100 text-xs">{data?.type}</p>
            </div>
            <Target className="h-12 w-12 text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Days Remaining</p>
              <p className="text-3xl font-bold text-white">
                {data?.end_date ? getDaysRemaining() : 'N/A'}
              </p>
              <p className="text-purple-100 text-xs">
                {data?.end_date ? 'until deadline' : 'no deadline set'}
              </p>
            </div>
            <Timer className="h-12 w-12 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Progress Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Progress Trends</h3>
          <button 
            onClick={fetchProgressData}
            className="flex items-center text-blue-600 hover:text-blue-700"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </button>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString('en-GB')}
                formatter={(value, name) => [
                  typeof value === 'number' ? value.toLocaleString() : value,
                  type === 'challenge' ? 'Total Progress' : 'Progress'
                ]}
              />
              <Line 
                type="monotone" 
                dataKey={type === 'challenge' ? 'totalProgress' : 'progress'} 
                stroke={chartColors.primary} 
                strokeWidth={3}
                dot={{ fill: chartColors.primary, strokeWidth: 2, r: 6 }}
              />
              {type === 'goal' && (
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke={chartColors.accent} 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: chartColors.accent, strokeWidth: 2, r: 4 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Challenge/Goal Details */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {type === 'challenge' ? 'Challenge' : 'Goal'} Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Trophy className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm text-gray-500">Title</p>
                <p className="font-medium text-gray-900">{data?.title}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Target className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">Target</p>
                <p className="font-medium text-gray-900">{data?.target_value} {data?.type}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-medium text-gray-900 capitalize">{data?.duration}</p>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-500">Period</p>
                <p className="font-medium text-gray-900">
                  {new Date(data?.start_date).toLocaleDateString()} - {new Date(data?.end_date).toLocaleDateString()}
                </p>
              </div>
            </div>
            {type === 'challenge' && (
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-500">Group</p>
                  <p className="font-medium text-gray-900">{data?.group_name}</p>
                </div>
              </div>
            )}
            {type === 'goal' && (
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-500">User</p>
                  <p className="font-medium text-gray-900">{data?.user_name}</p>
                  <p className="text-sm text-gray-500">{data?.user_city}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-gray-600">{data?.description}</p>
        </div>
      </div>
    </div>
  );

  const renderLeaderboardTab = () => {
    if (type !== 'challenge') return null;
    
    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Participant Leaderboard</h3>
          <div className="space-y-4">
            {participants.map((participant, index) => (
              <div key={participant.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center">
                    {participant.rank <= 3 ? (
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                        participant.rank === 1 ? 'bg-yellow-100 text-yellow-600' :
                        participant.rank === 2 ? 'bg-gray-100 text-gray-600' :
                        'bg-orange-100 text-orange-600'
                      }`}>
                        {participant.rank === 1 ? <Crown className="h-5 w-5" /> :
                         participant.rank === 2 ? <Medal className="h-5 w-5" /> :
                         <Award className="h-5 w-5" />}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold">
                        {participant.rank}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full font-bold">
                    {participant.avatar}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{participant.name}</p>
                    <p className="text-sm text-gray-500">{participant.city}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg text-gray-900">{participant.progress.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">{participant.percentage.toFixed(1)}% complete</p>
                  <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(participant.percentage, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top 3 Podium */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Top Performers</h3>
          <div className="flex items-end justify-center space-x-8">
            {participants.slice(0, 3).map((participant, index) => (
              <div key={participant.id} className="text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg mb-2 ${
                  index === 0 ? 'bg-yellow-500' :
                  index === 1 ? 'bg-gray-400' :
                  'bg-orange-500'
                }`}>
                  {participant.avatar}
                </div>
                <div className={`w-20 rounded-t-lg flex items-center justify-center text-white font-bold ${
                  index === 0 ? 'h-24 bg-yellow-500' :
                  index === 1 ? 'h-20 bg-gray-400' :
                  'h-16 bg-orange-500'
                }`}>
                  <span className="text-2xl">{index + 1}</span>
                </div>
                <p className="font-medium text-gray-900 mt-2">{participant.name}</p>
                <p className="text-sm text-gray-500">{participant.progress.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Daily Average</p>
              <p className="text-2xl font-bold text-gray-900">
                {type === 'challenge' 
                  ? (progressData.length > 0 ? Math.round(progressData.reduce((sum, day) => sum + day.totalProgress, 0) / progressData.length).toLocaleString() : '0')
                  : (progressData.length > 0 ? (progressData[progressData.length - 1]?.progress / progressData.length).toFixed(1) : '0')
                }
              </p>
            </div>
            <Activity className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Best Day</p>
              <p className="text-2xl font-bold text-gray-900">
                {type === 'challenge'
                  ? (progressData.length > 0 ? Math.max(...progressData.map(d => d.totalProgress - (progressData[progressData.indexOf(d) - 1]?.totalProgress || 0))).toLocaleString() : '0')
                  : (progressData.length > 0 ? Math.max(...progressData.map((d, i) => d.progress - (progressData[i - 1]?.progress || 0))).toFixed(1) : '0')
                }
              </p>
            </div>
            <Zap className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">{calculateProgress().toFixed(1)}%</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Daily Progress Bar Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Daily Progress Breakdown</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={progressData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString('en-GB')}
              />
              <Bar 
                dataKey={type === 'challenge' ? 'totalProgress' : 'progress'} 
                fill={chartColors.primary}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate('/challenges')}
            className="flex items-center text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Back to Challenges
          </button>
        </div>
        <div className="text-center py-12">
          <Trophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {type === 'challenge' ? 'Challenge' : 'Goal'} Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The {type === 'challenge' ? 'challenge' : 'goal'} you're looking for doesn't exist or couldn't be loaded.
          </p>
          <button 
            onClick={() => navigate('/challenges')}
            className="btn-primary"
          >
            Return to Challenges & Goals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate('/challenges')}
            className="flex items-center text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Back to Challenges
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{data?.title}</h2>
            <p className="text-gray-600 mt-1">
              {type === 'challenge' ? 'Challenge' : 'Goal'} Progress & Analytics
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            calculateProgress() >= 100 ? 'bg-green-100 text-green-800' :
            calculateProgress() >= 75 ? 'bg-blue-100 text-blue-800' :
            calculateProgress() >= 50 ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {calculateProgress() >= 100 ? 'Completed' :
             calculateProgress() >= 75 ? 'Excellent' :
             calculateProgress() >= 50 ? 'Good Progress' :
             'Getting Started'}
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          {type === 'challenge' && (
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leaderboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leaderboard
            </button>
          )}
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Analytics
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'leaderboard' && renderLeaderboardTab()}
      {activeTab === 'analytics' && renderAnalyticsTab()}
    </div>
  );
};

export default ProgressView; 